import PropTypes from 'prop-types';
import qs from 'qs';
import React, { useState, useEffect } from 'react';
import algoliasearch from 'algoliasearch/lite';
import { InstantSearch, Configure } from 'react-instantsearch';
import { searchStateToUrl, urlToSearchState } from '../../helpers/search-url';
import SearchBox from './components/SearchBox';
import InfiniteHits from './components/InfiniteHits';
import IndexMenu from './components/IndexMenu';
import Stats from './components/Stats';
import RefinementList from './components/RefinementList';
import MenuSelect from './components/MenuSelect';
import ClearRefinements from './components/ClearRefinements';
import NoResults from './components/NoResults';

const algoliaClient = algoliasearch(process.env.ALGOLIA_APP_ID, window.algolia.key);
const searchClient = {
    ...algoliaClient,
    search(requests) {
        const newRequests = requests.map((request) => {
            // test for empty string and change request parameter: analytics
            if (!request.params.query || request.params.query.length === 0) {
                request.params.analytics = false;
            }
            return request;
        });
        return algoliaClient.search(newRequests);
    },
};

const queryParams = qs.parse(window.location.search, { ignoreQueryPrefix: true });
const initialIndex = queryParams.view || 'all';
const initialUiState = {
    [`${process.env.ENVIRONMENT}_${initialIndex}`]: urlToSearchState(queryParams),
};

const GlobalSearch = ({ userId }) => {
    const [indexState, setIndexState] = useState(initialIndex);
    const indexWithPrefix = `${process.env.ENVIRONMENT}_${indexState}`;

    useEffect(() => {
        const currentUrl = new URL(window.location.href);
        const params = new URLSearchParams(currentUrl.search);
        params.set('view', indexState);

        if (indexState === 'all') {
            params.delete('eventType');
            params.delete('categories');
            params.delete('topic');
            params.delete('menu[year]');
        } else if (indexState === 'events') {
            params.delete('categories');
            params.delete('topic');
            params.delete('menu[year]');
        } else if (indexState === 'insights') {
            params.delete('eventType');
            params.delete('menu[year]');
        } else if (indexState === 'news') {
            params.delete('eventType');
            params.delete('categories');
            params.delete('topic');
        }

        const updatedUrl = `${currentUrl.origin}${currentUrl.pathname}?${params.toString()}`;
        window.history.pushState({}, '', updatedUrl);
    }, [indexState]);

    return (
        <InstantSearch
            searchClient={searchClient}
            indexName={indexWithPrefix}
            initialUiState={initialUiState}
            insights={{
                insightsInitParams: {
                    useCookie: true,
                    userToken: userId || undefined,
                },
            }}
            onStateChange={({ uiState, setUiState }) => {
                setUiState(uiState);

                const indexUiState = Object.entries(uiState)[0][1];
                const urlState = searchStateToUrl(indexUiState);
                const queryString = `?${qs.stringify(
                    { ...urlState, view: indexState },
                    {
                        arrayFormat: 'comma',
                        encoder: (str) => {
                            return encodeURIComponent(str).replace(/%20/g, '+');
                        },
                    }
                )}`;
                window.history.pushState({}, '', queryString);
            }}
        >
            <Configure hitsPerPage={10} clickAnalytics />
            <SearchBox showLoadingIndicator />

            <div className="container filters__wrapper">
                <IndexMenu
                    indexState={indexState}
                    onIndexChange={(index) => {
                        setIndexState(index);
                    }}
                />
                <Stats />
            </div>

            <div className="container">
                <div className="cardsListings">
                    {indexState === 'all' && (
                        <div className="cards__sidebar cards__sidebar--search" style={{ borderBottom: '0' }} />
                    )}
                    {indexState === 'events' && (
                        <div>
                            <ClearRefinements />
                            <RefinementList attribute="eventType" label="Event Type" />
                        </div>
                    )}
                    {indexState === 'insights' && (
                        <div>
                            <ClearRefinements />
                            <RefinementList attribute="categories.title" label="Content Types" />
                            <RefinementList attribute="topics" label="Featured Topics" />
                        </div>
                    )}
                    {indexState === 'news' && (
                        <div>
                            <ClearRefinements />
                            <MenuSelect attribute="year" />
                        </div>
                    )}
                    <div className="cards__listing">
                        <InfiniteHits />
                        <NoResults />
                    </div>
                </div>
            </div>
        </InstantSearch>
    );
};

GlobalSearch.propTypes = {
    userId: PropTypes.string,
};

export default GlobalSearch;
